import React, { useState, useEffect, useMemo } from 'react';
import Layout from "../../../Component/Layout";
import AsyncSelect from "react-select/async";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import CharacteristicsModal from "../../../Component/CharacteristicsModal ";
import { ORDER_API } from "../../../apiConfig";
import { FileText } from 'lucide-react';

function CRM() {
    const [loading, setLoading] = useState(false);
    const [complaintData, setComplaintData] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const MANUAL_PATH = "/assets/files/Y(Single core)_Datasheet (2).pdf";
    
    // Filters
    const [filterCustomer, setFilterCustomer] = useState(null);
    
    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [modalDetails, setModalDetails] = useState([]);
    const [activeComplaintId, setActiveComplaintId] = useState("");
    
    // Resolution State
    const [complaintStatus, setComplaintStatus] = useState("Open");
    const [responsiblePerson, setResponsiblePerson] = useState("");
    const [solutionProvided, setSolutionProvided] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    
    // Tab & Char Modal State
    const [activeTab, setActiveTab] = useState("basic");
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);

    useEffect(() => {
        fetchComplaints();
        fetchUsers();
    }, []);

    const fetchComplaints = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${ORDER_API}/crm/complaints`);
            if (res.ok) {
                const data = await res.json();
                setComplaintData(data.data); 
            } else {
                toast.error("Failed to load complaints");
            }
        } catch (err) {
            console.error("Failed to load complaints:", err);
            toast.error("Error fetching data");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await fetch(`${ORDER_API}/crm/users`);
            if (res.ok) {
                const data = await res.json();
                setUsersList(data.data);
            }
        } catch (err) {
            console.error("Failed to load users:", err);
        }
    };

    const loadCustomerOptions = async (inputValue) => {
        try {
            const res = await fetch(`${ORDER_API}/crm/customers`);
            const data = await res.json();
            const options = data.data.map(c => ({
                value: c.partner_code,
                label: `${c.partner_code} - ${c.vendor_name}`,
            }));

            if (!inputValue) return options;
            const search = inputValue.toLowerCase();
            return options.filter(opt => opt.label.toLowerCase().includes(search));
        } catch (err) {
            return [];
        }
    };

    const filteredData = useMemo(() => {
        return complaintData.filter((item) => {
            return (
                filterCustomer === null || 
                item.customer === filterCustomer.label || 
                (item.customer && item.customer.includes(filterCustomer.label))
            );
        });
    }, [complaintData, filterCustomer]);

    const handleOpenModal = async (complaint_id) => {
        try {
            const res = await fetch(`${ORDER_API}/crm/complaint-details/${complaint_id}`);
            if (res.ok) {
                const data = await res.json();
                setModalDetails(data.data);
                setActiveComplaintId(complaint_id);
                setActiveTab("basic");
                
                if (data.length > 0) {
                    setComplaintStatus(data[0].complaint_status || "Open");
                    setResponsiblePerson(data[0].responsible_person || "");
                    setSolutionProvided(data[0].solution_provided || "");
                }
                
                setShowModal(true);
            } else {
                toast.error("Failed to fetch complaint details");
            }
        } catch (err) {
            console.error("Error fetching details:", err);
            toast.error("Error fetching details");
        }
    };

    const handleUpdateComplaint = async () => {
        setIsUpdating(true);
        try {
            const res = await fetch(`${ORDER_API}/crm/update/${activeComplaintId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    status: complaintStatus,
                    responsible_person: responsiblePerson,
                    solution_provided: solutionProvided
                })
            });

            if (res.ok) {
                toast.success("Complaint updated successfully!");
                setShowModal(false);
                fetchComplaints(); 
            } else {
                toast.error("Failed to update complaint");
            }
        } catch (error) {
            console.error("Update Error:", error);
            toast.error("Internal Server Error");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleOpenCharModal = (productData) => {
        let chars = [];
        try {
            chars = typeof productData.characteristics === 'string' 
                ? JSON.parse(productData.characteristics) 
                : productData.characteristics || [];
        } catch (e) {
            chars = [];
        }

        const modalData = chars.map(c => ({
            characteristic: c.characteristic,
            value: c.value !== undefined ? c.value : (productData[c.characteristic] || "")
        }));
        
        setSelectedCharacteristics(modalData);
        setModalVisible(true);
    };

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0";
        return val.toLocaleString('en-IN');
    };

    const basicInfo = modalDetails.length > 0 ? modalDetails[0] : {};

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
    <div>
        <div style={styles.pageTitle}>Customer Relationship Management</div>
        <div style={styles.pageSubtitle}>Manage and review open complaints</div>
    </div>
                        <button
                        onClick={() => {
                            setManualExists(null);
                            setShowManual(true);
                            fetch(MANUAL_PATH, { method: "HEAD" })
                                .then((res) => {
                                    const contentType = res.headers.get("content-type") || "";
                                    setManualExists(res.ok && contentType.includes("pdf"));
                                })
                                .catch(() => setManualExists(false));
                        }}
                        title="View User Manual"
        style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            color: "#3b82f6",
            fontSize: "14px",
            fontWeight: "600",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
    >
        <FileText size={18} />
        User Manual
    </button>
</div>

{showManual && (
    <div
        onClick={() => setShowManual(false)}
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
        }}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            style={{
                background: "#ffffff",
                borderRadius: "12px",
                width: "97%",
                height: "96%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "14px 20px",
                    borderBottom: "1px solid #e2e8f0",
                }}
            >
                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                    User Manual
                </h4>
                <button
                    onClick={() => setShowManual(false)}
                    style={{
                        border: "none",
                        background: "#f1f5f9",
                        borderRadius: "8px",
                        width: "32px",
                        height: "32px",
                        cursor: "pointer",
                        fontSize: "18px",
                        color: "#475569",
                        lineHeight: 1,
                    }}
                    title="Close"
                >
                    ×
                </button>
            </div>

                                    {manualExists === null && (
                            <div style={{
                                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#94a3b8", fontSize: "14px"
                            }}>
                                Checking for user manual...
                            </div>
                        )}

                        {manualExists === false && (
                            <div style={{
                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                            }}>
                                <FileText size={40} color="#cbd5e1" />
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                    User Manual Not Available
                                </div>
                                <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                </div>
                            </div>
                        )}

                        {manualExists === true && (
                            <iframe
                                src={`${MANUAL_PATH}#zoom=170`}
                                title="User Manual PDF"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                }}
                            />
                        )}
        </div>
    </div>
)}

            <div style={styles.filterCard}>
                <div className="row g-4">
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>Customer</label>
                        <AsyncSelect
                            cacheOptions
                            loadOptions={loadCustomerOptions}
                            defaultOptions
                            value={filterCustomer}
                            onChange={setFilterCustomer}
                            placeholder="Select Customer"
                            isClearable={true}
                            styles={{
                                control: (base) => ({ ...base, minHeight: 36, fontSize: '13px' }),
                                menu: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                    </div>
                </div>
            </div>

            <div style={styles.tableContainer}>
                {loading && <div style={{ padding: 20 }}>Loading…</div>}
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>Complaint Number</th>
                            <th style={styles.erpTh}>Billing ID</th>
                            <th style={styles.erpTh}>Customer Name</th>
                            <th style={styles.erpTh}>SO ID</th>
                            <th style={styles.erpTh}>Total Price</th>
                            <th style={styles.erpTh}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length ? filteredData.map(item => (
                            <tr key={item.complaint_id}>
                                <td style={{ ...styles.erpTd, fontWeight: 600 }}>{item.complaint_id}</td>
                                <td style={styles.erpTd}>{item.bi_id}</td>
                                <td style={styles.erpTd}>{item.customer}</td>
                                <td style={styles.erpTd}>{item.so_id}</td>
                                <td style={{ ...styles.erpTd, fontWeight: 500 }}>
                                    ₹ {formatCurrency(item.total_price)}
                                </td>
                                <td style={styles.erpTd}>
                                    <button 
                                        className="btn btn-outline-dark btn-sm"
                                        style={{ fontSize: 11 }}
                                        onClick={() => handleOpenModal(item.complaint_id)}
                                    >
                                        View
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="6" className="text-center p-3 text-muted">No Open Complaints Found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Main Details Modal */}
            {showModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1200px", width: "90%", maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '1.2rem' }}>
                                Complaint Details: {activeComplaintId}
                            </div>
                            <button style={styles.iconBtn} onClick={() => setShowModal(false)}>✕</button>
                        </div>
                        
                        <div style={styles.modalBody}>
                            <div style={styles.tabs}>
                                {['basic', 'product'].map(t => (
                                    <button
                                        key={t}
                                        className={`btn ${activeTab === t ? "btn-secondary" : "btn-light"}`}
                                        onClick={() => setActiveTab(t)}
                                        style={styles.compactTabBtn}
                                    >
                                        {t.charAt(0).toUpperCase() + t.slice(1)}
                                    </button>
                                ))}
                            </div>

                            {activeTab === "basic" && (
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Billing ID</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.bi_id || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Customer</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.customer || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Sales Organization</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.sales_organization || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Division</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.division || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Distribution Channel</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.distribution_channel || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Payment Terms</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.payment_terms || "-"}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "product" && (
                                <div>
                                    <div style={{ ...styles.tableContainer, padding: 0, overflowX: 'auto' }}>
                                        <table className="table" style={styles.erpTable}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                                    <th style={{ ...styles.erpTh, width: '40px' }}>Item</th>
                                                    <th style={styles.erpTh}>Product Code</th>
                                                    <th style={styles.erpTh}>Description</th>
                                                    <th style={styles.erpTh}>Class</th>
                                                    <th style={styles.erpTh}>Batch Number</th>
                                                    <th style={styles.erpTh}>UOM</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Qty</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Total Price</th>
                                                    <th style={styles.erpTh}>Comment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {modalDetails.length > 0 ? modalDetails.map((row, idx) => (
                                                    <tr key={idx}>
                                                        <td style={styles.erpTd}>{idx + 1}</td>
                                                        <td style={styles.erpTd}>{row.product_code}</td>
                                                        <td style={styles.erpTd}>{row.description}</td>
                                                        <td style={styles.erpTd}>
                                                            {row.product_class || 'N/A'}
                                                            <span 
                                                                style={{ cursor: 'pointer', marginLeft: 8, color: '#151616' }} 
                                                                onClick={() => handleOpenCharModal(row)}
                                                                title="View Characteristics"
                                                            >
                                                                ▶
                                                            </span>
                                                        </td>
                                                        <td style={{ ...styles.erpTd, color: '#0369a1' }}>
                                                            {row.batch_number || 'N/A'}
                                                        </td>
                                                        <td style={styles.erpTd}>{row.uom}</td>
                                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>{row.delivery_qty}</td>
                                                        <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 600 }}>
                                                            ₹ {formatCurrency(row.total_price || (row.unit_price * row.delivery_qty))}
                                                        </td>
                                                        <td style={{ ...styles.erpTd, maxWidth: '150px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                            {row.complaint_comment || '-'}
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan="9" className="text-center p-3 text-muted">No products found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                        <h6 style={{ fontWeight: 600, color: '#1e293b', marginBottom: '12px' }}>Complaint Resolution</h6>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <label style={styles.compactLabel}>Responsible Person</label>
                                                <select 
                                                    className="form-control" 
                                                    style={styles.compactInput}
                                                    value={responsiblePerson}
                                                    onChange={(e) => setResponsiblePerson(e.target.value)}
                                                >
                                                    <option value="">Select User</option>
                                                    {usersList.map((u, idx) => (
                                                        <option key={idx} value={u.full_name}>{u.full_name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label style={styles.compactLabel}>Solution Provided</label>
                                                <select 
                                                    className="form-control" 
                                                    style={styles.compactInput}
                                                    value={solutionProvided}
                                                    onChange={(e) => setSolutionProvided(e.target.value)}
                                                >
                                                    <option value="">Select Solution</option>
                                                    <option value="Refund">Refund</option>
                                                    <option value="Return">Return</option>
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label style={styles.compactLabel}>Status</label>
                                                <select 
                                                    className="form-control" 
                                                    style={styles.compactInput}
                                                    value={complaintStatus}
                                                    onChange={(e) => setComplaintStatus(e.target.value)}
                                                >
                                                    <option value="Open">Open</option>
                                                    <option value="In Progress">In Progress</option>
                                                    <option value="Resolved">Resolved</option>
                                                    <option value="Closed">Closed</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Close</button>
                            {activeTab === 'product' && (
                                <button 
                                    className="btn btn-dark" 
                                    onClick={handleUpdateComplaint}
                                    disabled={isUpdating}
                                >
                                    {isUpdating ? "Updating..." : "Update Complaint"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <CharacteristicsModal
                modalVisible={modalVisible}
                selectedCharacteristics={selectedCharacteristics}
                isEditing={false} 
                handleChange={() => {}} 
                handleClose={() => setModalVisible(false)}
            />

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />
        </Layout>
    );
}

const styles = {
    compactLabel: { fontSize: "13px", fontWeight: 600, marginBottom: "4px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "34px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "13px", padding: "4px 10px" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
    filterLabel: { fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 26, fontWeight: 700, color: '#1e293b', marginBottom: 2 },
    pageSubtitle: { fontSize: 13, color: '#64748b' },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};

export default CRM;